import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Layout } from '../components/Layout';
import { usePageTitle } from '../hooks/usePageTitle';

export function Register() {
  usePageTitle('Create Account');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const register = useAuthStore((s) => s.register);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const ok = await register({ name, email, password });
      if (ok) {
        navigate('/dashboard');
      } else {
        setError('Registration failed. This email may already be in use.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto mt-16 sm:mt-20 px-6 pb-20">
        <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-2">
          Create Account
        </h1>
        <p className="text-mustard-charcoal/60 mb-8">
          Join LifeSpark360
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-mustard-charcoal mb-1">
              Full Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-mustard-charcoal mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-mustard-charcoal mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none"
            />
            <p className="text-xs text-mustard-charcoal/50 mt-1">Minimum 8 characters</p>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-mustard-green-dark text-white py-3 rounded-lg font-medium hover:bg-mustard-green transition-colors disabled:opacity-50"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center mt-6 text-sm text-mustard-charcoal/60">
          Already have an account?{' '}
          <Link to="/login" className="text-mustard-green font-medium hover:underline">
            Sign In
          </Link>
        </p>
      </div>
    </Layout>
  );
}
